/**
 * The Model vs Actual page's arithmetic that is not the model itself: levers,
 * immutable edits, the three-way series every chart draws, and year totals.
 * Kept out of the components so it can be tested without rendering.
 */

import { PLANS } from '../config/scienceModel'

/** Copy of `obj` with `value` at `path` (an array of keys and indices). */
export function setIn(obj, path, value) {
  if (path.length === 0) return value
  const [head, ...rest] = path
  const copy = Array.isArray(obj) ? [...obj] : { ...obj }
  copy[head] = setIn(obj?.[head], rest, value)
  return copy
}

/**
 * The sheet's assumptions with Manifold's live prices laid over them, cell by
 * cell. A cell Manifold has no price for keeps the sheet's, and is reported,
 * so a gap in the price list reads as a fallback rather than as the model
 * silently mixing two sources.
 *
 * @returns {{ assumptions: object, sources: object, differences: object[] }}
 *   sources[molecule][plan][book] is 'live' or 'sheet'
 */
export function withLivePrices(a, live) {
  const sources = {}
  const differences = []
  const prices = Object.fromEntries(Object.entries(a.prices).map(([molecule, plans]) => [
    molecule,
    Object.fromEntries(Object.entries(plans).map(([plan, books]) => [
      plan,
      Object.fromEntries(Object.entries(books).map(([book, sheetPrice]) => {
        const livePrice = live?.[molecule]?.[plan]?.[book]
        const source = livePrice != null ? 'live' : 'sheet'
        sources[molecule] = sources[molecule] || {}
        sources[molecule][plan] = sources[molecule][plan] || {}
        sources[molecule][plan][book] = source
        if (source === 'live' && Math.abs(livePrice - sheetPrice) >= 0.005) {
          differences.push({ molecule, plan, book, sheet: sheetPrice, live: livePrice })
        }
        return [book, source === 'live' ? livePrice : sheetPrice]
      })),
    ])),
  ]))
  return { assumptions: { ...a, prices }, sources, differences }
}

export const NEUTRAL_LEVERS = { spend: 1, cac: 1, price: 1, retention: 1 }

/**
 * Assumptions with the headline levers applied on top.
 *
 * Levers scale whole curves at once — "what if CAC is 20% worse everywhere" —
 * so the month-by-month edits underneath survive a lever being moved and put
 * back. Retention scales renewals only: age 0 is the purchase, which always
 * happens, and no rate can exceed everyone.
 */
export function applyLevers(a, levers) {
  const { spend = 1, cac = 1, price = 1, retention = 1 } = levers || {}
  const scale = (value, k) => value * k
  return {
    ...a,
    acquisition: {
      ...a.acquisition,
      startingSpend: scale(a.acquisition.startingSpend, spend),
      cacPaid: a.acquisition.cacPaid.map(v => scale(v, cac)),
      cacOrganic: a.acquisition.cacOrganic.map(v => scale(v, cac)),
    },
    prices: Object.fromEntries(Object.entries(a.prices).map(([molecule, plans]) => [
      molecule,
      Object.fromEntries(Object.entries(plans).map(([plan, books]) => [
        plan,
        Object.fromEntries(Object.entries(books).map(([book, v]) => [book, scale(v, price)])),
      ])),
    ])),
    retention: Object.fromEntries(PLANS.map(plan => [
      plan,
      (a.retention[plan] || []).map((rate, age) => (age === 0 ? rate : Math.min(1, rate * retention))),
    ])),
  }
}

/** "Sep '26" */
export function monthLabel(month) {
  const [year, mon] = month.split('-').map(Number)
  const name = new Date(Date.UTC(year, mon - 1, 1)).toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
  return `${name} '${String(year).slice(2)}`
}

/**
 * One point per model month carrying all three readings of `field`.
 *
 * `actual` is null wherever nothing has happened yet, so a chart draws no bar
 * rather than a zero one; `partial` marks the month still in progress, whose
 * bar is to-date while the re-forecast point is paced to the whole month.
 */
export function threeWaySeries(plan, recorded, reforecast, field, ramped = null) {
  const read = (row) => (typeof field === 'function' ? field(row) : row?.[field])
  return plan.map((planRow, i) => {
    const recordedRow = recorded?.[i]
    const happened = recordedRow && recordedRow.source !== 'forecast'
    return {
      month: planRow.month,
      label: monthLabel(planRow.month),
      plan: read(planRow),
      actual: happened ? read(recordedRow) : null,
      reforecast: reforecast ? read(reforecast[i]) : null,
      ramped: ramped ? read(ramped[i]) : null,
      partial: recordedRow?.source === 'paced',
    }
  })
}

/** Change from `base` to `value` as a fraction, or null when there is no base. */
export function variance(value, base) {
  if (value == null || base == null || base === 0) return null
  return (value - base) / Math.abs(base)
}

/**
 * Totals per calendar year, then for the whole horizon.
 *
 * Sums only. A ratio (CAC, margin) cannot be summed across months, so callers
 * re-derive those from the summed parts.
 */
export function yearTotals(rows, fields) {
  const groups = new Map()
  for (const row of rows) {
    const year = row.month.slice(0, 4)
    if (!groups.has(year)) groups.set(year, [])
    groups.get(year).push(row)
  }
  const sum = (list, field) => {
    const values = list.map(r => (typeof field === 'function' ? field(r) : r[field])).filter(v => v != null)
    return values.length ? values.reduce((a, b) => a + b, 0) : null
  }
  const totalOf = (key, list) => ({
    key,
    ...Object.fromEntries(Object.entries(fields).map(([name, field]) => [name, sum(list, field)])),
  })
  return [...[...groups.entries()].map(([year, list]) => totalOf(year, list)), totalOf('Total', rows)]
}

/** Money, compact for axes: $1.2M, $850K, $42. */
export function formatMoneyCompact(value) {
  if (value == null || !Number.isFinite(value)) return '—'
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}M`
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}K`
  return `${sign}$${abs.toFixed(0)}`
}

export function formatMoney(value) {
  if (value == null || !Number.isFinite(value)) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
}

export function formatCount(value) {
  if (value == null || !Number.isFinite(value)) return '—'
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)
}

export function formatCountCompact(value) {
  if (value == null || !Number.isFinite(value)) return '—'
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value)
}

export function formatPercent(value, digits = 1) {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${(value * 100).toFixed(digits)}%`
}

/** A signed variance: "+12.4%", "−38.0%". */
export function formatVariance(value) {
  if (value == null || !Number.isFinite(value)) return '—'
  const pct = (value * 100).toFixed(1)
  return value > 0 ? `+${pct}%` : value < 0 ? `−${pct.slice(1)}%` : '0.0%'
}

export const FORMATTERS = {
  money: { full: formatMoney, compact: formatMoneyCompact },
  count: { full: formatCount, compact: formatCountCompact },
  percent: { full: formatPercent, compact: v => formatPercent(v, 0) },
}
